import React, { useState, useMemo } from 'react';
import { useLocale, formatCurrency } from '@platform/core';
import { useBudget } from '../../contexts/BudgetContext';
import { ProjectStatRow } from '../../types/budget.types';

// Helper to format currency safely (empty string causes Intl.NumberFormat to crash)
const formatAmount = (amount: number, currency?: string) => {
  try {
    return formatCurrency(amount, currency || undefined);
  } catch {
    return amount.toLocaleString();
  }
};

type StatType = 'pm' | 'manager' | 'client';

export const ProjectStatistics: React.FC = () => {
  const { t } = useLocale();
  const { filteredProjects, calculateProjectProgress, currentBudget } = useBudget();
  const [activeTab, setActiveTab] = useState<StatType>('pm');

  const currency = currentBudget?.currency;

  // Calculate statistics grouped by different fields
  const statistics = useMemo(() => {
    const pmStats = new Map<string, ProjectStatRow>();
    const managerStats = new Map<string, ProjectStatRow>();
    const clientStats = new Map<string, ProjectStatRow>();

    filteredProjects.forEach(project => {
      const progress = calculateProjectProgress(project);

      // PM Statistics
      const pm = project.projectManager || t('common.noData');
      if (!pmStats.has(pm)) {
        pmStats.set(pm, { name: pm, projectCount: 0, totalBudget: 0, totalProfit: 0, profitPercent: 0 });
      }
      const pmStat = pmStats.get(pm)!;
      pmStat.projectCount++;
      pmStat.totalBudget += project.budget || 0;
      pmStat.totalProfit += progress.profit;

      // Manager Statistics
      const manager = project.manager || t('common.noData');
      if (!managerStats.has(manager)) {
        managerStats.set(manager, { name: manager, projectCount: 0, totalBudget: 0, totalProfit: 0, profitPercent: 0 });
      }
      const managerStat = managerStats.get(manager)!;
      managerStat.projectCount++;
      managerStat.totalBudget += project.budget || 0;
      managerStat.totalProfit += progress.profit;

      // Client Statistics
      const client = project.client || t('common.noData');
      if (!clientStats.has(client)) {
        clientStats.set(client, { name: client, projectCount: 0, totalBudget: 0, totalProfit: 0, profitPercent: 0 });
      }
      const clientStat = clientStats.get(client)!;
      clientStat.projectCount++;
      clientStat.totalBudget += project.budget || 0;
      clientStat.totalProfit += progress.profit;
    });

    // Calculate profit percentages
    const calcProfitPercent = (stats: Map<string, ProjectStatRow>) => {
      stats.forEach(stat => {
        stat.profitPercent = stat.totalBudget > 0
          ? Math.round((stat.totalProfit / stat.totalBudget) * 100)
          : 0;
      });
      return Array.from(stats.values()).sort((a, b) => b.totalBudget - a.totalBudget);
    };

    return {
      pm: calcProfitPercent(pmStats),
      manager: calcProfitPercent(managerStats),
      client: calcProfitPercent(clientStats),
    };
  }, [filteredProjects, calculateProjectProgress, t]);

  const activeStats = statistics[activeTab];

  const totals = useMemo(() => {
    return activeStats.reduce(
      (acc, stat) => ({
        projectCount: acc.projectCount + stat.projectCount,
        totalBudget: acc.totalBudget + stat.totalBudget,
        totalProfit: acc.totalProfit + stat.totalProfit,
      }),
      { projectCount: 0, totalBudget: 0, totalProfit: 0 }
    );
  }, [activeStats]);

  const totalProfitPercent = totals.totalBudget > 0
    ? Math.round((totals.totalProfit / totals.totalBudget) * 100)
    : 0;

  if (filteredProjects.length === 0) {
    return null;
  }

  return (
    <div className="project-statistics">
      <div className="stats-header">
        <h3>{t('budget.projects.statistics')}</h3>
        <div className="stats-tabs">
          <button
            className={`stats-tab ${activeTab === 'pm' ? 'active' : ''}`}
            onClick={() => setActiveTab('pm')}
          >
            {t('budget.projects.pmStatistics')}
          </button>
          <button
            className={`stats-tab ${activeTab === 'manager' ? 'active' : ''}`}
            onClick={() => setActiveTab('manager')}
          >
            {t('budget.projects.managerStatistics')}
          </button>
          <button
            className={`stats-tab ${activeTab === 'client' ? 'active' : ''}`}
            onClick={() => setActiveTab('client')}
          >
            {t('budget.projects.clientStatistics')}
          </button>
        </div>
      </div>

      <div className="stats-table-wrapper">
        <table className="stats-table">
          <thead>
            <tr>
              <th>{activeTab === 'pm' ? t('budget.projects.projectManager') : activeTab === 'manager' ? t('budget.projects.manager') : t('budget.projects.client')}</th>
              <th className="text-center">{t('budget.projects.numberOfProjects')}</th>
              <th className="text-end">{t('budget.projects.totalBudget')}</th>
              <th className="text-end">{t('budget.projects.totalProfit')}</th>
              <th className="text-end">{t('budget.projects.profitPercent')}</th>
            </tr>
          </thead>
          <tbody>
            {activeStats.map((stat) => (
              <tr key={stat.name}>
                <td>{stat.name}</td>
                <td className="text-center">{stat.projectCount}</td>
                <td className="text-end">{formatAmount(stat.totalBudget, currency)}</td>
                <td className={`text-end ${stat.totalProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                  {formatAmount(stat.totalProfit, currency)}
                </td>
                <td className={`text-end ${stat.profitPercent >= 0 ? 'text-success' : 'text-danger'}`}>
                  {stat.profitPercent}%
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>{t('common.total')}</strong></td>
              <td className="text-center"><strong>{totals.projectCount}</strong></td>
              <td className="text-end"><strong>{formatAmount(totals.totalBudget, currency)}</strong></td>
              <td className={`text-end ${totals.totalProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                <strong>{formatAmount(totals.totalProfit, currency)}</strong>
              </td>
              <td className={`text-end ${totalProfitPercent >= 0 ? 'text-success' : 'text-danger'}`}>
                <strong>{totalProfitPercent}%</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <style>{`
        .project-statistics {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow);
          margin-top: 2rem;
          overflow: hidden;
        }

        .stats-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem;
          border-bottom: 1px solid var(--color-border);
          background: var(--color-background);
          flex-wrap: wrap;
          gap: 1rem;
        }

        .stats-header h3 {
          margin: 0;
          font-size: 1rem;
        }

        .stats-tabs {
          display: flex;
          gap: 0.5rem;
        }

        .stats-tab {
          padding: 0.375rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .stats-tab:hover {
          background: var(--color-border);
        }

        .stats-tab.active {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }

        .stats-table-wrapper {
          overflow-x: auto;
        }

        .stats-table {
          width: 100%;
          border-collapse: collapse;
        }

        .stats-table th,
        .stats-table td {
          padding: 0.75rem 1rem;
          text-align: start;
          border-bottom: 1px solid var(--color-border);
        }

        .stats-table th {
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          color: var(--color-text-secondary);
          background: var(--color-background);
        }

        .stats-table tbody tr:hover {
          background: var(--color-background);
        }

        .stats-table tfoot {
          background: var(--color-background);
        }

        .stats-table tfoot td {
          border-top: 2px solid var(--color-border);
        }

        .text-center {
          text-align: center;
        }

        .text-end {
          text-align: end;
        }

        .text-success {
          color: #22c55e;
        }

        .text-danger {
          color: #ef4444;
        }
      `}</style>
    </div>
  );
};
